import { useCallback, useState } from 'react';
import * as yup from 'yup';

import useApiAction from '../../../hooks/useApiAction';
import { UploaderFile } from '../../common/Uploader/types';
import { useYupValidation } from '../../../hooks/useYupValidation';

type Params = {
  reason?: string;
  claimId: number;
  onSubmitSuccess: () => void;
};

type ActionState = {
  claimId: number;
  comment: string;
  files: UploaderFile[];
};

const schema = yup
  .object()
  .shape({
    comment: yup.string().optional().label('Label'),
    files: yup
      .array()
      .of(
        yup.object().shape({
          fileType: yup.string().required().label('Document type'),
        })
      )
      .optional(),
  })
  .test('comment-or-files', 'CLAIM_TAKE_ACTION_COMMENT_OR_FILE_IS_REQUIRED', (values) => {
    return !!values.comment || (values.files && values.files.length > 0);
  });

export function useTakeAction(params: Params) {
  const { reason, claimId, onSubmitSuccess } = params;
  const [action, setAction] = useState<ActionState>({
    claimId,
    comment: '',
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitCustomerAction] = useApiAction(
    // @ts-ignore
    (api) =>
      api.submitCustomerAction(claimId, {
        files: action.files.map((file) => ({
          id: file.response?.body.id,
          fileType: file.fileType,
        })),
        reason: reason || 'Other',
        comment: action.comment,
      }),
    [claimId, action, reason],
    {
      feedbackMessage: 'CUSTOMER_ACTION_SUCCESSFULLY_SUBMITTED_NOTICE',
    }
  );

  const { validate, errors } = useYupValidation(schema);

  const submit = useCallback(async () => {
    if (!validate(action)) {
      return;
    }

    setIsSubmitting(true);
    // @ts-ignore
    const { success } = await submitCustomerAction();

    if (success) {
      onSubmitSuccess();
    }

    setIsSubmitting(false);
  }, [submitCustomerAction, onSubmitSuccess, validate, action]);

  const onChange = useCallback((value: string, name: string) => setAction((prev) => ({ ...prev, [name]: value })), [
    setAction,
  ]);

  return {
    action,
    onChange,
    errors,
    submit,
    isSubmitting,
  };
}
